import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { billingDiscountProblems, BillingLineState, toDiscountStates } from '../../../components/Billing/BillingLinesEditor';
import { DiscountState } from '../../../components/Billing/DiscountRow';
import { parseDiscountValue } from '../../../utilities/helper/discount.helper';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { GeneralStatusEnum } from '../../../common/enums/common.enum';
import { BillingItemDto, ChildBillingProfileDto } from '../../../common/interfaces/invoicing.interface';
import { getActiveBillingItems, getChildBillingProfile, upsertChildBillingProfile } from '../../../api/invoicing.api';
import { createNotification } from '../../../utilities/helper/notification.helper';

/** What a line contributes to the saved arrangement, so display-only fields never count as an edit. */
const savedShape = (lines: BillingLineState[], discounts: DiscountState[]) =>
  JSON.stringify([
    lines.map(line => [line.billingItemId, line.quantity ?? 1, line.unitPriceNetOverride, line.vatRateOverride, line.pdfLabelOverride]),
    discounts.map(discount => [discount.label.trim(), discount.mode, parseDiscountValue(discount.valueText), discount.appliesTo])
  ]);

/**
 * Editing a child's own arrangement: the catalogue to pick from, the open arrangement to
 * start from, and saving it as a new dated one.
 *
 * Shared by the arrangement modal and the contract panel, which edit the same thing from
 * two places; two copies of the seeding below would sooner or later disagree about which
 * values count as overrides.
 */
export const useChildBillingProfileEditor = (kindergartenId: string, childId: string, defaultValidFrom: string) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState<BillingItemDto[]>([]);
  const [current, setCurrent] = useState<ChildBillingProfileDto | null>(null);
  const [initialLines, setInitialLines] = useState<BillingLineState[]>([]);
  const [lines, setLines] = useState<BillingLineState[]>([]);
  const [initialDiscounts, setInitialDiscounts] = useState<DiscountState[]>([]);
  const [discounts, setDiscounts] = useState<DiscountState[]>([]);
  // set by a save that found a problem, so every discount says what is wrong with it
  const [showErrors, setShowErrors] = useState(false);
  const [validFrom, setValidFrom] = useState(defaultValidFrom);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [items, profile] = await Promise.all([getActiveBillingItems(kindergartenId), getChildBillingProfile(kindergartenId, childId)]);
      const catalogue = items.results ?? [];
      setCatalog(catalogue);
      setCurrent(profile.results ?? null);
      setNote(profile.results?.note ?? '');

      // seed from the open arrangement so the admin edits what is in force rather than
      // starting from nothing and silently dropping half of it
      const seeded = (profile.results?.items ?? []).map(item => {
        const fromCatalog = catalogue.find(entry => entry.id === item.billingItemId);
        return {
          billingItemId: item.billingItemId,
          quantity: item.quantity,
          unitPriceNetOverride: item.overriddenFields.includes('unitPriceNet') ? item.unitPriceNet : null,
          vatRateOverride: item.overriddenFields.includes('vatRate') ? item.vatRate : null,
          pdfLabelOverride: item.overriddenFields.includes('pdfLabel') ? item.pdfLabel : null,
          sortOrder: item.sortOrder,
          name: item.name,
          type: item.type,
          catalogPrice: fromCatalog?.unitPriceNet ?? item.unitPriceNet,
          catalogVat: fromCatalog?.vatRate ?? item.vatRate,
          catalogLabel: fromCatalog?.pdfLabel ?? item.pdfLabel,
          isInactive: item.status !== GeneralStatusEnum.ACTIVE
        };
      });
      setInitialLines(seeded);
      setLines(seeded);
      const seededDiscounts = toDiscountStates(profile.results?.discounts ?? []);
      setInitialDiscounts(seededDiscounts);
      setDiscounts(seededDiscounts);
    } catch {
      createNotification(MESSAGE_TYPE.ERROR, t('billing.profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [kindergartenId, childId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = useMemo(
    () => savedShape(lines, discounts) !== savedShape(initialLines, initialDiscounts),
    [lines, discounts, initialLines, initialDiscounts]
  );

  /** The translation key of what stops a save, or null when it can go ahead. */
  const validationError = (): string | null => {
    if (!lines.length) return 'billing.profile.validation.atLeastOneItem';
    if (!validFrom) return 'billing.profile.validation.startDateRequired';
    if (Object.keys(billingDiscountProblems(t, lines, discounts)).length) {
      setShowErrors(true);
      return 'discounts.validation.fixFirst';
    }
    return null;
  };

  const save = () =>
    upsertChildBillingProfile(kindergartenId, childId, {
      validFrom,
      note: note.trim() || undefined,
      items: lines.map((line, index) => ({
        billingItemId: line.billingItemId,
        quantity: line.quantity ?? 1,
        unitPriceNetOverride: line.unitPriceNetOverride,
        vatRateOverride: line.vatRateOverride,
        pdfLabelOverride: line.pdfLabelOverride,
        sortOrder: index
      })),
      discounts: discounts.map((discount, index) => ({
        label: discount.label.trim(),
        mode: discount.mode,
        value: parseDiscountValue(discount.valueText) as number,
        appliesToBillingItemId: discount.appliesTo,
        sortOrder: index
      }))
    });

  return {
    loading,
    catalog,
    current,
    lines,
    setLines,
    discounts,
    setDiscounts,
    showErrors,
    validFrom,
    setValidFrom,
    note,
    setNote,
    isDirty,
    validationError,
    save
  };
};
